import * as THREE from 'three';
import { LoadingIndicator } from '../ui/LoadingIndicator.js';
import { gameConfig } from '../config/gameConfig.js';
import { Dice } from './Dice.js';
import { Grenade } from './Grenade.js';
import { TeleportDice } from './TeleportDice.js';

export class Board {
    constructor(scene) {
        this.scene = scene;
        this.size = gameConfig.boardSizes.small; // 使用small尺寸(4x4)作为默认棋盘大小
        this.matrix = Array(this.size).fill().map(() => Array(this.size).fill(null));
        
        this.initializeBoard();
    }

    async initializeDice() {
        console.log("Initializing dice...");
        this.clearBoard();
        
        // 创建加载指示器
        const loadingIndicator = new LoadingIndicator();
        loadingIndicator.show();
        
        const totalDice = this.size * this.size;
        let completedDice = 0;
        
        // 生成和放置骰子
        const dropPromises = [];
        for (let col = 0; col < this.size; col++) {
            for (let row = 0; row < this.size; row++) {
                // 添加列延迟
                const colDelay = col * 50;
                await new Promise(resolve => setTimeout(resolve, colDelay));
                
                // 生成骰子
                const dice = await this.generateDiceAt(row, col);
                this.matrix[row][col] = dice;
                dice.mesh.position.y = gameConfig.dice.initialDropHeight;
                
                // 更新进度
                completedDice++;
                const progress = (completedDice / totalDice) * 100;
                loadingIndicator.updateProgress(progress, '正在生成并放置骰子...');
                
                // 立即开始下落动画
                const targetHeight = dice.isBomb ? gameConfig.dice.bomb.height : gameConfig.dice.finalHeight;
                const dropPromise = this.animateDiceMove(dice, targetHeight);
                dropPromises.push(dropPromise);
            }
        }
        
        // 等待所有下落动画完成
        await Promise.all(dropPromises);
        
        console.log("所有骰子下落完成，时间：", new Date().toLocaleTimeString());
        console.log("Dice initialization complete");
        
        // 隐藏加载指示器
        loadingIndicator.hide();
    }

    async generateDiceAt(row, col) {
        const value = Math.floor(Math.random() * 6) + 1;
        // 使用区间划分的方式计算骰子类型
        const randomValue = Math.random();
        let diceType = 'normal';
        let totalProbability = 0;

        // 计算炸弹骰子概率区间
        if (gameConfig.dice.bomb.enabled) {
            totalProbability += gameConfig.dice.bomb.probability;
            if (randomValue < totalProbability) {
                diceType = 'bomb';
            }
        }

        // 计算传送骰子概率区间
        if (diceType === 'normal' && gameConfig.dice.teleport.enabled) {
            if (randomValue < totalProbability + gameConfig.dice.teleport.probability) {
                diceType = 'teleport';
            }
            totalProbability += gameConfig.dice.teleport.probability;
        }
        
        console.log(`生成骰子 位置(${row}, ${col}):`, {
            随机值: randomValue.toFixed(4),
            炸弹概率: gameConfig.dice.bomb.probability,
            传送概率: gameConfig.dice.teleport.probability,
            骰子类型: diceType,
            点数: value
        });
        
        // 根据类型创建不同的实例
        let dice;
        if (diceType === 'bomb') {
            dice = new Grenade();
            console.log(`创建炸弹骰子 位置(${row}, ${col})`);
        } else if (diceType === 'teleport') {
            dice = new TeleportDice(value);
            console.log(`创建传送骰子 位置(${row}, ${col}), 点数: ${value}`);
        } else {
            dice = new Dice(value);
            console.log(`创建普通骰子 位置(${row}, ${col}), 点数: ${value}`);
        }
        
        // 等待mesh加载完成
        await new Promise(resolve => {
            const checkMesh = () => {
                if (dice.mesh) {
                    // 根据骰子类型设置不同的缩放
                    if (dice.isBomb) {
                        // 使用手榴弹专用缩放，并确保旋转角度正确
                        dice.mesh.scale.set(gameConfig.dice.bomb.scale, gameConfig.dice.bomb.scale, gameConfig.dice.bomb.scale);
                        dice.mesh.rotation.z = Math.PI / 2;  // 保持手榴弹的正确朝向
                        console.log(`设置炸弹骰子缩放: ${gameConfig.dice.bomb.scale}`);
                    } else {
                        // 普通骰子和传送骰子使用标准缩放
                        dice.mesh.scale.set(gameConfig.dice.scale, gameConfig.dice.scale, gameConfig.dice.scale);
                        console.log(`设置${dice.isTeleport ? '传送' : '普通'}骰子缩放: ${gameConfig.dice.scale}`);
                    }
                    resolve();
                } else {
                    setTimeout(checkMesh, 100);
                }
            };
            checkMesh();
        });
        
        const spacing = gameConfig.dice.spacing;
        const offset = (this.size - 1) * spacing / 2;
        const x = col * spacing - offset;
        const z = row * spacing - offset;
        
        // 根据骰子类型设置不同的高度
        const height = dice.isBomb ? gameConfig.dice.bomb.height : gameConfig.dice.finalHeight;
        dice.mesh.position.set(x, height, z);
        this.scene.scene.add(dice.mesh);
        
        console.log(`骰子创建完成 位置(${row}, ${col}):`, {
            类型: dice.isBomb ? '炸弹' : (dice.isTeleport ? '传送' : '普通'),
            坐标: {
                x: x.toFixed(2),
                y: height.toFixed(2),
                z: z.toFixed(2)
            }
        });
        
        return dice;
    }

    async animateDiceMove(dice, targetY) {
        const startY = dice.mesh.position.y;
        const duration = gameConfig.dice.animationDuration;
        const startTime = Date.now();
        
        // 添加水平方向的随机偏移
        const randomOffsetX = (Math.random() - 0.5) * 0.5;
        const randomOffsetZ = (Math.random() - 0.5) * 0.5;
        const originalX = dice.mesh.position.x;
        const originalZ = dice.mesh.position.z;

        return new Promise(resolve => {
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeProgress = this.easeOutBounce(progress);
                
                // 计算当前高度
                dice.mesh.position.y = startY + (targetY - startY) * easeProgress;
                
                // 添加水平方向的摆动效果
                const swingProgress = Math.sin(progress * Math.PI);
                dice.mesh.position.x = originalX + randomOffsetX * swingProgress;
                dice.mesh.position.z = originalZ + randomOffsetZ * swingProgress;
                

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    // 恢复原始位置
                    dice.mesh.position.x = originalX;
                    dice.mesh.position.z = originalZ;
                    dice.mesh.position.y = targetY;
                    resolve();
                }
            };
            animate();
        });
    }



    initializeBoard() {
        this.matrix = Array(this.size).fill().map(() => Array(this.size).fill(null));
    }

    clearBoard() {
        // 先初始化matrix数组
        this.matrix = Array(this.size).fill().map(() => Array(this.size).fill(null));
        
        // 清理现有的骰子
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                const dice = this.matrix[row][col];
                if (dice && dice.mesh) {
                    this.scene.scene.remove(dice.mesh);
                }
            }
        }
    }

    getDiceAt(row, col) {
        return (row >= 0 && row < this.size && col >= 0 && col < this.size) ? this.matrix[row][col] : null;
    }

    removeDice(dice) {
        // 如果是传送骰子，先移除其圆环效果和粒子系统
        if (dice.isTeleport) {
            // 移除圆环效果
            if (dice.teleportRing && dice.ringAddedToScene) {
                this.scene.scene.remove(dice.teleportRing);
                console.log('已移除传送骰子的圆环效果');
            }
            
            // 移除粒子系统
            if (dice.particleSystem && dice.particlesAddedToScene) {
                this.scene.scene.remove(dice.particleSystem);
                console.log('已移除传送骰子的粒子系统');
            }
            
            // 重置相关状态
            dice.ringAddedToScene = false;
            dice.particlesAddedToScene = false;
            dice.teleportRing = null;
            dice.particleSystem = null;
        }

        // 移除骰子模型
        if (dice.mesh) {
            this.scene.scene.remove(dice.mesh);
        }
        
        // 从矩阵中移除骰子引用
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (this.matrix[row][col] === dice) {
                    this.matrix[row][col] = null;
                    return;
                }
            }
        }
    }

    async applyGravity() {
        console.log('开始生成新骰子...');
        const dropPromises = [];
        
        // 遍历每一列
        for (let col = 0; col < this.size; col++) {
            let emptySpaces = [];
            let filledSpaces = [];
            
            // 从底部向上计算空白位置和已填充位置
            for (let row = this.size - 1; row >= 0; row--) {
                const currentDice = this.matrix[row][col];
                if (!currentDice) {
                    emptySpaces.push(row);
                } else {
                    filledSpaces.push({
                        dice: currentDice,
                        row: row
                    });
                }
            }
            
            // 如果有空白位置，先将现有骰子下移
            if (emptySpaces.length > 0) {
                // 从底部开始重新排列现有骰子
                let currentRow = this.size - 1;
                for (const filled of filledSpaces) {
                    if (filled.row !== currentRow) {
                        // 更新骰子位置
                        this.matrix[currentRow][col] = filled.dice;
                        this.matrix[filled.row][col] = null;
                        
                        // 计算新的位置并添加动画
                        const spacing = gameConfig.dice.spacing;
                        const offset = (this.size - 1) * spacing / 2;
                        const x = col * spacing - offset;
                        const z = currentRow * spacing - offset;
                        
                        filled.dice.mesh.position.x = x;
                        filled.dice.mesh.position.z = z;
                        // 根据骰子类型设置不同的高度
                        const height = filled.dice.isBomb ? gameConfig.dice.bomb.height : gameConfig.dice.finalHeight;
                        filled.dice.mesh.position.y = height;
                        dropPromises.push(this.animateDiceMove(filled.dice, height));
                    }
                    currentRow--;
                }
                
                // 在顶部空白位置生成新骰子
                for (let i = 0; i < emptySpaces.length; i++) {
                    const row = i;
                    // 添加列延迟
                    const colDelay = col * 50;
                    await new Promise(resolve => setTimeout(resolve, colDelay));
                    
                    // 生成新骰子
                    const dice = await this.generateDiceAt(row, col);
                    this.matrix[row][col] = dice;
                    dice.mesh.position.y = gameConfig.dice.initialDropHeight;
                    
                    // 立即开始下落动画
                    const targetHeight = dice.isBomb ? gameConfig.dice.bomb.height : gameConfig.dice.finalHeight;
                    console.log(`在位置(${row}, ${col})生成新骰子，从高度${gameConfig.dice.initialDropHeight}下落到${targetHeight}`);
                    const dropPromise = this.animateDiceMove(dice, targetHeight);
                    dropPromises.push(dropPromise);
                }
            }
            
            console.log(`第${col}列有${emptySpaces.length}个空白位置需要填充`);
        }
        
        // 等待所有新骰子完成下落动画
        console.log('等待所有新骰子完成下落动画...');
        await Promise.all(dropPromises);
        console.log('重力效果应用完成');


        this.printBoardState('补充新骰子后的棋盘状态')
    }

    async animateDiceMove(dice, targetY) {
        const startY = dice.mesh.position.y;
        const duration = gameConfig.dice.animationDuration;
        const startTime = Date.now();
        
        // 添加水平方向的随机偏移
        const randomOffsetX = (Math.random() - 0.5) * 0.5;
        const randomOffsetZ = (Math.random() - 0.5) * 0.5;
        const originalX = dice.mesh.position.x;
        const originalZ = dice.mesh.position.z;

        return new Promise(resolve => {
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easeProgress = this.easeOutBounce(progress);
                
                // 计算当前高度
                dice.mesh.position.y = startY + (targetY - startY) * easeProgress;
                
                // 添加水平方向的摆动效果
                const swingProgress = Math.sin(progress * Math.PI);
                dice.mesh.position.x = originalX + randomOffsetX * swingProgress;
                dice.mesh.position.z = originalZ + randomOffsetZ * swingProgress;
                

                if (progress < 1) {
                    requestAnimationFrame(animate);
                } else {
                    // 恢复原始位置
                    dice.mesh.position.x = originalX;
                    dice.mesh.position.z = originalZ;
                    dice.mesh.position.y = targetY;
                    resolve();
                }
            };
            animate();
        });
    }

    // 处理传送骰子与目标骰子的位置交换
    async swapDicePositions(teleportDice, targetDice) {
        if (teleportDice.isAnimating || targetDice.isAnimating) {
            console.log('骰子正在动画中，无法进行传送');
            return false;
        }

        // 找到两个骰子在矩阵中的位置
        let teleportPos = null;
        let targetPos = null;

        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                const dice = this.matrix[row][col];
                if (dice === teleportDice) {
                    teleportPos = { row, col };
                } else if (dice === targetDice) {
                    targetPos = { row, col };
                }

                if (teleportPos && targetPos) break;
            }
            if (teleportPos && targetPos) break;
        }

        if (!teleportPos || !targetPos) {
            console.error('无法找到骰子位置');
            return false;
        }

        // 获取矩阵中实际的骰子对象
        const actualTeleportDice = this.matrix[teleportPos.row][teleportPos.col];
        const actualTargetDice = this.matrix[targetPos.row][targetPos.col];

        console.log(`开始传送: 从(${teleportPos.row}, ${teleportPos.col})到(${targetPos.row}, ${targetPos.col})`);

        // 保存原始位置和旋转状态
        const teleportOriginalPosition = actualTeleportDice.mesh.position.clone();
        const targetOriginalPosition = actualTargetDice.mesh.position.clone();
        const teleportOriginalRotation = actualTeleportDice.mesh.rotation.clone();
        const targetOriginalRotation = actualTargetDice.mesh.rotation.clone();

        // 创建传送动画路径
        const midPoint = new THREE.Vector3()
            .addVectors(teleportOriginalPosition, targetOriginalPosition)
            .multiplyScalar(0.5);
        midPoint.y += gameConfig.dice.teleport.pathHeight; // 设置弧线高度

        // 创建传送轨迹曲线
        const curve1 = new THREE.QuadraticBezierCurve3(
            teleportOriginalPosition,
            midPoint,
            targetOriginalPosition
        );
        const curve2 = new THREE.QuadraticBezierCurve3(
            targetOriginalPosition,
            midPoint,
            teleportOriginalPosition
        );

        // 执行传送动画
        const duration = gameConfig.dice.teleport.teleportDuration;
        const startTime = Date.now();

        // 同时执行两个骰子的动画
        await Promise.all([
            new Promise(resolve => {
                const animate = () => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeProgress = 1 - Math.pow(1 - progress, 3); // 缓动效果

                    // 更新传送骰子位置
                    const position = curve1.getPointAt(easeProgress);
                    actualTeleportDice.mesh.position.copy(position);

                    // 传送骰子旋转效果
                    actualTeleportDice.mesh.rotation.y += 0.1;

                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    } else {
                        // 确保最终位置和旋转状态准确
                        actualTeleportDice.mesh.position.copy(targetOriginalPosition);
                        actualTeleportDice.mesh.rotation.copy(teleportOriginalRotation);
                        resolve();
                    }
                };
                animate();
            }),
            new Promise(resolve => {
                const animate = () => {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    const easeProgress = 1 - Math.pow(1 - progress, 3); // 缓动效果

                    // 更新目标骰子位置
                    const position = curve2.getPointAt(easeProgress);
                    actualTargetDice.mesh.position.copy(position);

                    // 目标骰子旋转效果
                    actualTargetDice.mesh.rotation.y += 0.1;

                    if (progress < 1) {
                        requestAnimationFrame(animate);
                    } else {
                        // 确保最终位置和旋转状态准确
                        actualTargetDice.mesh.position.copy(teleportOriginalPosition);
                        actualTargetDice.mesh.rotation.copy(targetOriginalRotation);
                        resolve();
                    }
                };
                animate();
            })
        ]);

        // 更新矩阵中的位置
        this.matrix[teleportPos.row][teleportPos.col] = actualTargetDice;
        this.matrix[targetPos.row][targetPos.col] = actualTeleportDice;

        console.log('传送完成');
        // 打印传送后的棋盘状态
        this.printBoardState('传送后的棋盘状态');

        return true;
    }

    easeOutBounce(x) {
        const n1 = 7.5625;
        const d1 = 2.75;

        if (x < 1 / d1) {
            return n1 * x * x;
        } else if (x < 2 / d1) {
            return n1 * (x -= 1.5 / d1) * x + 0.75;
        } else if (x < 2.5 / d1) {
            return n1 * (x -= 2.25 / d1) * x + 0.9375;
        } else {
            return n1 * (x -= 2.625 / d1) * x + 0.984375;
        }
    }

    // 检查棋盘是否已满
    isBoardFull() {
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (!this.matrix[row][col]) {
                    return false;
                }
            }
        }
        return true;
    }

    // 打印棋盘状态
    printBoardState(description = '') {
        console.log(description ? `${description}:` : '当前棋盘状态：');
        for (let row = 0; row < this.size; row++) {
            let rowStr = '';
            for (let col = 0; col < this.size; col++) {
                const dice = this.matrix[row][col];
                rowStr += dice ? (dice.isBomb ? '[G]' : (dice.isTeleport ? `[T${dice.topValue}]` : `[${dice.topValue}]`)) : '[_]';
            }
            console.log(`第${row}行: ${rowStr}`);
        }
    }

    // 检查是否有可合并的骰子
    hasMergePossibility() {
        // 水平检查
        for (let row = 0; row < this.size; row++) {
            let currentValue = null;
            let count = 0;
            for (let col = 0; col < this.size; col++) {
                const dice = this.matrix[row][col];
                if (!dice) {
                    currentValue = null;
                    count = 0;
                    continue;
                }
                
                if (dice.topValue === currentValue) {
                    count++;
                    if (count >= 3) return true;
                } else {
                    currentValue = dice.topValue;
                    count = 1;
                }
            }
        }

        // 垂直检查
        for (let col = 0; col < this.size; col++) {
            let currentValue = null;
            let count = 0;
            for (let row = 0; row < this.size; row++) {
                const dice = this.matrix[row][col];
                if (!dice) {
                    currentValue = null;
                    count = 0;
                    continue;
                }
                
                if (dice.topValue === currentValue) {
                    count++;
                    if (count >= 3) return true;
                } else {
                    currentValue = dice.topValue;
                    count = 1;
                }
            }
        }

        return false;
    }

    // 检查是否有空位
    hasEmptySpaces() {
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (!this.matrix[row][col]) {
                    return true;
                }
            }
        }
        return false;
    }

    checkMergeable() {
        let allMatchGroups = [];

        // 水平检查
        for (let row = 0; row < this.size; row++) {
            let currentGroup = [];
            for (let col = 0; col < this.size; col++) {
                const dice = this.matrix[row][col];
                
                if (!dice) {
                    // 如果当前组达到3个或以上，加入匹配组
                    if (currentGroup.length >= 3) {
                        allMatchGroups.push([...currentGroup]);
                    }
                    // 重置当前组
                    currentGroup = [];
                } else {
                    // 检查当前骰子是否与组中的骰子点数相同
                    if (currentGroup.length === 0 || dice.topValue === currentGroup[0].topValue) {
                        currentGroup.push(dice);
                    } else {
                        // 如果当前组达到3个或以上，加入匹配组
                        if (currentGroup.length >= 3) {
                            allMatchGroups.push([...currentGroup]);
                        }
                        // 重置当前组，将当前骰子作为新组的第一个
                        currentGroup = [dice];
                    }
                }
            }
            // 检查行末尾的组
            if (currentGroup.length >= 3) {
                allMatchGroups.push(currentGroup);
            }
        }

        // 垂直检查
        for (let col = 0; col < this.size; col++) {
            let currentGroup = [];
            for (let row = 0; row < this.size; row++) {
                const dice = this.matrix[row][col];
                
                if (!dice) {
                    // 如果当前组达到3个或以上，加入匹配组
                    if (currentGroup.length >= 3) {
                        allMatchGroups.push([...currentGroup]);
                    }
                    // 重置当前组
                    currentGroup = [];
                } else {
                    // 检查当前骰子是否与组中的骰子点数相同
                    if (currentGroup.length === 0 || dice.topValue === currentGroup[0].topValue) {
                        currentGroup.push(dice);
                    } else {
                        // 如果当前组达到3个或以上，加入匹配组
                        if (currentGroup.length >= 3) {
                            allMatchGroups.push([...currentGroup]);
                        }
                        // 重置当前组，将当前骰子作为新组的第一个
                        currentGroup = [dice];
                    }
                }
            }
            // 检查列末尾的组
            if (currentGroup.length >= 3) {
                allMatchGroups.push(currentGroup);
            }
        }

        // 如果没有找到任何可合并的组，返回null
        if (allMatchGroups.length === 0) {
            return null;
        }

        // 将所有可合并的骰子组合并成一个数组返回
        return allMatchGroups.flat();
    }

    // 获取炸弹骰子周围的骰子
    getBombAffectedDices(bombDice) {
        const affectedDices = [];
        let bombPosition = null;

        // 找到炸弹骰子的位置
        for (let row = 0; row < this.size; row++) {
            for (let col = 0; col < this.size; col++) {
                if (this.matrix[row][col] === bombDice) {
                    bombPosition = { row, col };
                    break;
                }
            }
            if (bombPosition) break;
        }

        if (!bombPosition) return [];

        // 获取爆炸范围内的所有骰子
        const range = gameConfig.dice.bomb.range;
        const checkedPositions = new Set(); // 用于记录已检查过的位置
        const bombsToExplode = []; // 用于存储需要连锁爆炸的手榴弹

        // 递归函数：检查指定位置的爆炸范围
        const checkExplosionRange = (centerRow, centerCol) => {
            for (let r = -range; r <= range; r++) {
                for (let c = -range; c <= range; c++) {
                    const newRow = centerRow + r;
                    const newCol = centerCol + c;
                    
                    // 检查位置是否在棋盘范围内
                    if (newRow >= 0 && newRow < this.size && newCol >= 0 && newCol < this.size) {
                        // 创建位置标识符
                        const posKey = `${newRow},${newCol}`;
                        
                        // 如果这个位置已经检查过，跳过
                        if (checkedPositions.has(posKey)) continue;
                        
                        // 标记这个位置已检查
                        checkedPositions.add(posKey);
                        
                        const dice = this.matrix[newRow][newCol];
                        if (dice && dice !== bombDice) {
                            affectedDices.push(dice);
                            // 如果是手榴弹，加入待爆炸列表
                            if (dice.type === 'grenade') {
                                bombsToExplode.push({
                                    dice: dice,
                                    row: newRow,
                                    col: newCol
                                });
                            }
                        }
                    }
                }
            }
        };

        // 检查初始手榴弹的爆炸范围
        checkExplosionRange(bombPosition.row, bombPosition.col);

        // 处理连锁爆炸
        while (bombsToExplode.length > 0) {
            const nextBomb = bombsToExplode.shift();
            checkExplosionRange(nextBomb.row, nextBomb.col);
        }

        return affectedDices;
    }

    async removeDiceGroup(dices) {
        // 检查是否包含炸弹骰子
        const bombDice = dices.find(dice => dice.isBomb);
        if (bombDice) {
            // 获取受影响的骰子
            const affectedDices = this.getBombAffectedDices(bombDice);
            // 将受影响的骰子添加到要移除的列表中
            dices = [...new Set([...dices, ...affectedDices])];
        }

            this.printBoardState('移除骰子前的棋盘状态')

        // 先为要移除的骰子添加高亮效果
        for (const dice of dices) {
            // 克隆原始材质并创建高亮效果
            const originalMaterial = dice.mesh.material;
            const highlightMaterial = new THREE.MeshPhongMaterial({
                color: new THREE.Color(0x8888ff),
                transparent: true,
                opacity: 0.8,
                shininess: 30
            });
            
            // 保存原始材质并应用高亮材质
            dice.originalMaterial = originalMaterial;
            dice.mesh.material = highlightMaterial;
        }

        // 等待一小段时间让玩家看到高亮效果
        await new Promise(resolve => setTimeout(resolve, 400)); // 增加显示时间

        // 先将所有要移除的骰子位置在matrix中标记为null
        for (const dice of dices) {
            for (let row = 0; row < this.size; row++) {
                for (let col = 0; col < this.size; col++) {
                    if (this.matrix[row][col] === dice) {
                        this.matrix[row][col] = null;
                        break;
                    }
                }
            }
        }

        // 执行淡出动画
        const animationPromises = dices.map(dice => this.animateDiceFadeOut(dice));
        await Promise.all(animationPromises);
        
        // 从场景中移除骰子
        for (const dice of dices) {
            if (dice.mesh) {
                this.removeDice(dice)
                // this.scene.scene.remove(dice.mesh);
            }
        }

        this.printBoardState('移除骰子后的棋盘状态')
    }

    async animateDiceFadeOut(dice) {
        const duration = 1200; // 增加动画持续时间
        const startTime = Date.now();
        const fragments = this.createDiceFragments(dice);
        
        // 创建爆炸中心点光源
        const light = new THREE.PointLight(0xffaa00, 2, 3);
        light.position.copy(dice.mesh.position);
        this.scene.scene.add(light);
        
        return new Promise(resolve => {
            const animate = () => {
                const elapsed = Date.now() - startTime;
                const progress = Math.min(elapsed / duration, 1);
                
                if (elapsed < duration) {
                    // 炸裂阶段
                    if (dice.mesh.visible) {
                        dice.mesh.visible = false;
                        fragments.forEach(frag => this.scene.scene.add(frag.mesh));
                    }
                    
                    const explosionProgress = elapsed / duration;
                    const lightIntensity = Math.max(0, 2 * (1 - explosionProgress * 2));
                    light.intensity = lightIntensity;
                    
                    // 更新骰子碎片
                    fragments.forEach(frag => {
                        // 更新碎片位置，使用非线性运动
                        const t = explosionProgress;
                        frag.velocity.y -= 0.015; // 增加重力效果
                        frag.position.add(frag.velocity);
                        
                        // 添加螺旋运动
                        const spiral = 0.1 * Math.sin(t * Math.PI * 4);
                        frag.position.x += spiral * Math.cos(frag.angle);
                        frag.position.z += spiral * Math.sin(frag.angle);
                        
                        // 更新旋转，增加变化幅度
                        frag.mesh.rotation.x += frag.rotationSpeed.x * (1 + t);
                        frag.mesh.rotation.y += frag.rotationSpeed.y * (1 + t);
                        frag.mesh.rotation.z += frag.rotationSpeed.z * (1 + t);
                        
                        // 更新颜色和透明度
                        const color = new THREE.Color();
                        color.setHSL(0.1 + 0.2 * t, 1, 0.5 + 0.5 * (1 - t));
                        frag.mesh.material.color = color;
                        frag.mesh.material.opacity = Math.max(0, 1 - explosionProgress * 1.2);
                        
                        // 更新碎片位置
                        frag.mesh.position.copy(frag.position);
                    });
                } else {
                    // 动画结束，清理碎片和光源
                    fragments.forEach(frag => this.scene.scene.remove(frag.mesh));
                    this.scene.scene.remove(light);
                    resolve();
                    return;
                }
                
                requestAnimationFrame(animate);
            };
            animate();
        });


    }
    
    createDiceFragments(dice) {
        const fragments = [];
        const fragmentCount = 12; // 增加碎片数量
        
        for (let i = 0; i < fragmentCount; i++) {
            // 随机选择碎片形状
            let geometry;
            const shapeType = Math.random();
            if (shapeType < 0.4) {
                // 不规则四面体
                geometry = new THREE.TetrahedronGeometry(0.15);
            } else if (shapeType < 0.7) {
                // 小立方体
                geometry = new THREE.BoxGeometry(0.2, 0.2, 0.2);
            } else {
                // 八面体
                geometry = new THREE.OctahedronGeometry(0.15);
            }
            
            // 创建发光材质
            const material = new THREE.MeshPhongMaterial({
                color: 0xffaa00,
                emissive: 0xff4400,
                emissiveIntensity: 0.5,
                transparent: true,
                opacity: 1,
                shininess: 50
            });
            
            const mesh = new THREE.Mesh(geometry, material);
            mesh.position.copy(dice.mesh.position);
            
            // 计算爆炸方向
            const angle = (Math.PI * 2 * i) / fragmentCount;
            const speed = 0.15 + Math.random() * 0.15; // 增加初始速度
            const upwardBias = 0.2 + Math.random() * 0.1; // 向上的偏移
            const velocity = new THREE.Vector3(
                Math.cos(angle) * speed,
                upwardBias,
                Math.sin(angle) * speed
            );
            
            // 随机旋转速度，增加变化范围
            const rotationSpeed = new THREE.Vector3(
                (Math.random() - 0.5) * 0.3,
                (Math.random() - 0.5) * 0.3,
                (Math.random() - 0.5) * 0.3
            );
            
            fragments.push({
                mesh,
                position: mesh.position,
                velocity,
                rotationSpeed,
                angle // 保存角度用于螺旋运动
            });
        }
        
        return fragments;
    }
}